import { BodyMedium, Pagination, SearchInput, ToggleButton, theme } from '@arnold/common';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  AnonymityLevel,
  Features,
  ReportOpenMetricsView,
  ReportSequenceSubscriptionSubscription,
  TopicGroupTypeCode,
  useGetProcessReportRespondentsLazyQuery,
} from '../../generated/hooks';
import { Filter } from '../../lib/reportHelper';
import { ReportOpenMetricsSender } from '../components/ObserverEventSender';
import ReportSection from '../components/ReportSection';
import RespondentsFeelings from '../components/RespondentsFeelings';
import { usePageParam } from '../../lib/reports/reportUtils';

const RESPONDENTS_ON_PAGE = 15;

type ReportSequenceData = NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>;

interface IParticipantTableProps {
  reportSequence: ReportSequenceData;
  publicAccess?: boolean;
  urlParams: string;
  accessToken: string;
  teamsFilter: string[] | null;
  onReload: (shoudReload?: boolean) => void;
  filters: Filter[];
  showOnlyUnread?: boolean;
  setShowOnlyUnread: (value: boolean) => void;
}

const StyledCard = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px ${theme.spacing.f};
  margin-bottom: ${theme.spacing.c};
  max-width: 58rem;
  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.g};
  }
`;

const RespondentsFeelingContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
  }
`;

const StyledSearchInput = styled(SearchInput)`
  width: 270px;
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-flow: column;
  align-items: end;
  @media (max-width: ${theme.breakpoint.small}) {
    align-self: flex-end;
    gap: ${theme.spacing.e};
  }
`;

const PaginationWrapper = styled.div`
  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    align-items: center;
  }
`;

enum INITAL_LOADING_STATE {
  BEFORE_FIRST_RENDER,
  AFTER_FIRST_RENDER_BEFORE_ANOTHER_LOADING,
  OWN_LOADING_STARTED,
}
const ParticipantTable = ({
  accessToken,
  reportSequence,
  publicAccess,
  urlParams,
  teamsFilter,
  onReload,
  filters,
  showOnlyUnread,
  setShowOnlyUnread,
}: IParticipantTableProps) => {
  const { t } = useTranslation(['Report', 'header']);
  const [currentPage, setCurrentPage] = usePageParam();
  const savedCheckedValue = localStorage.getItem(`hideRespondents:${accessToken}`);
  const [checked, setChecked] = useState(savedCheckedValue ? JSON.parse(savedCheckedValue) : false);
  const [searchQuery, setSearchQuery] = useState('');
  const container = useRef<HTMLDivElement>(null);
  const initialLoading = useRef<INITAL_LOADING_STATE>(INITAL_LOADING_STATE.BEFORE_FIRST_RENDER);

  const [loadData, { loading, error, data, refetch }] = useGetProcessReportRespondentsLazyQuery();

  // Always use data from the query if we're on a page other than page 1 or if we've started loading
  const useDate =
    (currentPage > 1 || initialLoading.current === INITAL_LOADING_STATE.OWN_LOADING_STARTED) &&
    data?.getProcessReportRespondents
      ? data.getProcessReportRespondents
      : reportSequence.groupedRespondents;

  const reloadRespondentsData = () => {
    // Always load data for the current page, even on first render
    initialLoading.current = INITAL_LOADING_STATE.OWN_LOADING_STARTED;

    if (data) {
      // If we already have data, use refetch to avoid unnecessary loading state
      refetch({
        accessToken,
        addedOnFrom: reportSequence.addedOnFrom ?? null,
        addedOnTo: reportSequence.addedOnTo ?? null,
        teamsFilter,
        pagination: {
          offset: (currentPage - 1) * RESPONDENTS_ON_PAGE,
          limit: RESPONDENTS_ON_PAGE,
        },
        searchQuery,
        withoutDeleted: checked,
        showOnlyUnread,
      });
    } else {
      // Initial load
      loadData({
        variables: {
          accessToken,
          addedOnFrom: reportSequence.addedOnFrom ?? null,
          addedOnTo: reportSequence.addedOnTo ?? null,
          teamsFilter,
          positionIds: filters
            .filter((f) => f.operator === 'position')
            .map((f) => f.values)
            .flat()
            .filter((f) => f) as string[],
          recruiterIds: filters
            .filter((f) => f.operator === 'recruiter')
            .map((f) => f.values)
            .flat()
            .filter((f) => f) as string[],
          managerIds: filters
            .filter((f) => f.operator === 'manager')
            .map((f) => f.values)
            .flat()
            .filter((f) => f) as string[],
          pagination: {
            offset: (currentPage - 1) * RESPONDENTS_ON_PAGE,
            limit: RESPONDENTS_ON_PAGE,
          },
          searchQuery,
          withoutDeleted: checked,
          showOnlyUnread,
        },
      });
    }
  };
  // Check if current page is more than total pages
  useEffect(() => {
    if (!useDate?.totalCount) return;
    const totalPages = Math.floor((useDate.totalCount - 1) / RESPONDENTS_ON_PAGE) + 1;
    if (currentPage > totalPages) {
      // Remove page param from URL if it is more than total pages
      setCurrentPage(1);
    }
  }, [useDate, currentPage, setCurrentPage]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reloadRespondentsData, [
    reportSequence,
    teamsFilter,
    searchQuery,
    checked,
    currentPage,
    loadData,
    accessToken,
    showOnlyUnread,
  ]);

  if (!reportSequence.surveyGroup) {
    return null;
  }

  if (error) {
    return <div>{error.message}</div>;
  }

  const handleChangeSearchQuery = (value: string) => {
    if (currentPage !== 1 && value !== searchQuery) {
      // Remove page param from URL when searching
      setCurrentPage(1);
    }
    setSearchQuery(value);
  };

  const handleToggleChange = (value: boolean) => {
    if (currentPage !== 1) {
      // Remove page param from URL when filtering
      setCurrentPage(1);
    }
    setChecked(value);
    localStorage.setItem(`hideRespondents:${accessToken}`, value.toString());
  };

  const handleToggleUnreadMessagesChange = (value: boolean) => {
    if (currentPage !== 1) {
      // Remove page param from URL when filtering
      setCurrentPage(1);
    }
    setShowOnlyUnread(value);
  };

  const isHiring = reportSequence.surveyGroup.topicGroup.typeCode === TopicGroupTypeCode.Hiring;

  return (
    <ReportSection
      passedRef={container}
      id={'respondentsTable'}
      header={isHiring ? t('respondentsHiringTable') : t('respondentsTable')}
      icomTag={'process-report-feelings-table-title'}
    >
      <RespondentsFeelingContainer>
        <StyledCard>
          <StyledSearchInput
            onChange={handleChangeSearchQuery}
            value={searchQuery}
            delay={500}
            name="respondents-search"
          />
          <ToggleContainer>
            <div className="d-flex ml-10" data-icom="hide-deleted-participants">
              <BodyMedium margin={`0 ${theme.spacing.d} 0 0`}>{t('hideDeletedParticipantsTitle')}</BodyMedium>
              <ToggleButton
                value={checked}
                className="mb-0"
                onChange={handleToggleChange}
                dataIcom={'hide-respondents-toggle'}
                dataCy={'hide-respondents-toggle'}
              />
            </div>
            <div className="d-flex ml-10" data-icom="show-unread-messages">
              <BodyMedium margin={`0 ${theme.spacing.d} 0 0`}>{t('showUnreadMessagesTitle')}</BodyMedium>
              <ToggleButton
                value={!!showOnlyUnread}
                className="mb-0"
                onChange={handleToggleUnreadMessagesChange}
                dataIcom={'show-unread-messages-toggle'}
              />
            </div>
          </ToggleContainer>
        </StyledCard>
        <RespondentsFeelings
          reportSequence={reportSequence}
          accessToken={accessToken}
          respondentLink={true}
          groupedRespondents={useDate?.data ?? []}
          urlParams={urlParams}
          reloadRespondentsData={reloadRespondentsData}
          publicAccess={!!publicAccess}
          currentPage={currentPage}
          loading={(initialLoading.current === INITAL_LOADING_STATE.OWN_LOADING_STARTED && loading) || !useDate}
          showDeleteButton={
            reportSequence.surveyGroup.steps.every(
              (step) => !step.processStep.stepTopicGroup.allowedAnonymities?.includes(AnonymityLevel.Organization),
            ) &&
            (reportSequence.metadata.organization.featureNames!.includes(
              Features.ReportRemoveRespondentFromProcessSurvey,
            ) ||
              !reportSequence.metadata.respondent)
          }
          onReload={onReload}
        />
        {!loading && !!useDate?.data.length && (
          <PaginationWrapper className="d-flex justify-content-between align-items-center">
            <ReportOpenMetricsSender
              reportAccessKey={accessToken}
              channelIds={
                useDate?.data.flatMap((gr) =>
                  gr.surveys.flatMap((s) => s.publicChannels.filter((ch) => ch.withAnswer).map((ch) => ch.id)),
                ) || []
              }
              reportView={ReportOpenMetricsView.ProcessTable}
              orgAdmin={!publicAccess}
            />

            <span style={{ marginTop: 20 }}>
              {t('paginationInfoText', {
                total: useDate.totalCount,
                from: (currentPage - 1) * RESPONDENTS_ON_PAGE + 1,
                to: Math.min(currentPage * RESPONDENTS_ON_PAGE, useDate.totalCount),
              })}
            </span>
            <Pagination
              totalPages={Math.floor((useDate.totalCount - 1) / RESPONDENTS_ON_PAGE) + 1}
              currentPage={currentPage}
              onPageChange={(page) => {
                setCurrentPage(page);
                const reportPage = document.querySelector('.report');
                if (!reportPage || !container.current) {
                  return;
                }
                const rect = container.current.getBoundingClientRect();
                const top = rect.top + reportPage.scrollTop;
                reportPage.scroll({
                  top: top - 260,
                  behavior: 'smooth',
                });
              }}
            />
          </PaginationWrapper>
        )}
      </RespondentsFeelingContainer>
    </ReportSection>
  );
};

export default ParticipantTable;
